import React, { useState } from 'react';
import { Star, Download, Clock, ShoppingBasket, FileText } from 'lucide-react';
import { formatDistanceToNow } from 'date-fns';
import { usePremium } from '../Subscription/PremiumProvider';

function PublicTemplateCard({ workspace, onImport, importingWorkspaceId }) {
  const { isPremium, openUpgradeModal } = usePremium();
  const [showFullDescription, setShowFullDescription] = useState(false);

  const handleImportClick = (e) => {
    e.preventDefault();
    e.stopPropagation();
    
    // If template is premium and user is not premium, show upgrade modal
    if (workspace.is_premium && !isPremium()) {
      openUpgradeModal();
      return;
    }

    onImport(workspace);
  };

  return (
    <div className="bg-white rounded-xl border-2 border-neutral-900 hover:shadow-lg transition-all duration-300 w-[320px] relative overflow-hidden">
      {workspace.is_premium && (
        <div className="absolute top-0 right-0">
          <div className="bg-gradient-to-r from-amber-500 to-yellow-500 text-white px-6 py-1 transform rotate-45 translate-x-7 translate-y-3">
            PRO
          </div>
        </div>
      )}

      <div className="p-4 flex flex-col h-[280px]">
        {/* Header - Fixed Height */}
        <div className="pb-3 min-h-[64px]">
          <div className="flex items-start justify-between">
            <div>
              <h3 className="text-lg font-semibold text-neutral-900 line-clamp-2">{workspace.name}</h3>
              <span className="inline-flex items-center px-2 py-0.5 mt-1 rounded-lg bg-neutral-100 text-neutral-600 text-xs">
                Template
              </span>
            </div>
            {workspace.popularity_score > 0 && (
              <div className="flex items-center space-x-1 text-amber-500 shrink-0 ml-2">
                <Star className="w-4 h-4 fill-current" />
                <span className="text-sm font-medium">
                  {(workspace.popularity_score / 20).toFixed(1)}
                </span>
              </div>
            )}
          </div>
        </div>

        {/* Content - Flexible Height with Min Height */}
        <div className="flex-1 flex flex-col min-h-[120px]">
          {/* Description with Tooltip */}
          <div className="min-h-[48px] mb-4 relative group">
            {workspace.description ? (
              <>
                <p className="text-sm text-neutral-600 line-clamp-2">
                  {workspace.description}
                </p>
                {/* Tooltip */}
                <div className="opacity-0 group-hover:opacity-100 transition-opacity absolute left-0 bottom-full mb-2 z-10">
                  <div className="bg-neutral-900 text-white p-3 rounded-lg shadow-lg max-w-[280px] text-sm">
                    {workspace.description}
                  </div>
                  {/* Tooltip Arrow */}
                  <div className="absolute -bottom-1 left-4 w-2 h-2 bg-neutral-900 transform rotate-45"></div>
                </div>
              </>
            ) : (
              <p className="text-sm text-neutral-400 italic">
                No description available
              </p>
            )}
          </div>

          {/* Stats - Fixed Height */}
          <div className="space-y-3">
            <div className="grid grid-cols-2 gap-4">
              <div className="flex items-center space-x-2 text-neutral-600">
                <Download className="w-4 h-4" />
                <span className="text-sm">
                  {workspace.download_count || 0} downloads
                </span>
              </div>
            </div>

            <div className="flex items-center space-x-4">
              <div className="flex items-center text-neutral-600">
                <ShoppingBasket className="h-4 w-4 mr-1.5 text-blue-600" />
                <span className="text-sm">
                  {workspace.basketCount} {workspace.basketCount === 1 ? 'basket' : 'baskets'}
                </span>
              </div>
              <div className="flex items-center text-neutral-600">
                <FileText className="h-4 w-4 mr-1.5 text-blue-600" />
                <span className="text-sm">
                  {workspace.topicCount} {workspace.topicCount === 1 ? 'topic' : 'topics'}
                </span>
              </div>
            </div>
          </div>
        </div>

        {/* Action Button - Fixed Position */}
        <button
          onClick={handleImportClick}
          disabled={importingWorkspaceId === workspace.id}
          className={`w-full mt-4 py-2.5 px-4 rounded-lg font-medium text-sm
            ${workspace.is_premium 
              ? 'bg-gradient-to-r from-amber-500 to-yellow-500 hover:from-amber-600 hover:to-yellow-600 text-white' 
              : 'bg-blue-600 hover:bg-blue-700 text-white'} 
            border border-neutral-900
            ${importingWorkspaceId === workspace.id ? 'opacity-50 cursor-not-allowed' : ''}
          `}
        >
          {importingWorkspaceId === workspace.id ? 'Importing...' : 'Import Workspace'}
        </button>
      </div>
    </div>
  );
}

export default PublicTemplateCard; 